import React, { useContext, useState } from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Chip,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined'
import PlaylistAddOutlinedIcon from '@mui/icons-material/PlaylistAddOutlined'
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined'
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined'
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined'
import AirlineSeatFlatOutlinedIcon from '@mui/icons-material/AirlineSeatFlatOutlined'
import PropaneTankOutlinedIcon from '@mui/icons-material/PropaneTankOutlined'
import DevicesOutlinedIcon from '@mui/icons-material/DevicesOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import { UserContext } from '../../../../App'
import {
  StatTile,
  dateTone,
  formatDate,
} from '../../vehicles/VehicleComponents/SectionKit'
import {
  canManageMakeReady,
  useMakeReadyTierAllowed,
} from '../../makeReadyModule/makeReadyAccess'
import { SEVERITY } from '../../makeReady/severity'
import { AssetContext } from './SingleAsset'
import useAssetActions from './useAssetActions'

const CATEGORY_ICONS = {
  'Medical Device': MonitorHeartOutlinedIcon,
  'Medical Bag': MedicalServicesOutlinedIcon,
  'Moving and Handling': AirlineSeatFlatOutlinedIcon,
  'Medical Gas': PropaneTankOutlinedIcon,
  'IT Equipment': DevicesOutlinedIcon,
}

// Page header for a single asset: identity, where it is and who has it, the
// record-keeping actions, and an at-a-glance tile row (servicing, defects,
// make ready).
const AssetHero = () => {
  const navigate = useNavigate()
  const { asset, makeReadyStatus, unavailability = [], openDialog, openTab } =
    useContext(AssetContext)
  const { user } = useContext(UserContext)
  const {
    canCheckIn,
    checkedOutToMe,
    checkIn,
    checkOutToMe,
    unassignVehicle,
    removeFromParent,
    removeFromStore,
  } = useAssetActions()
  const [moreAnchorEl, setMoreAnchorEl] = useState(null)

  const makeReadyAllowed = useMakeReadyTierAllowed(2)
  const canAssignLoadList = makeReadyAllowed && canManageMakeReady(user)
  const closeMore = () => setMoreAnchorEl(null)

  const CategoryIcon = CATEGORY_ICONS[asset.category] ?? Inventory2OutlinedIcon

  // Directly on a vehicle, inside another asset (e.g. a bag) - which may
  // itself be on a vehicle - or shelved in a kit store.
  const location = asset.vehicle
    ? {
        value: asset.vehicle.callSign,
        caption: 'On vehicle',
        chip: `On ${asset.vehicle.callSign}`,
      }
    : asset.parentAsset?.assetID
      ? {
          value: `In ${asset.parentAsset.assetID}`,
          caption: asset.parentAsset.vehicle?.callSign
            ? `On ${asset.parentAsset.vehicle.callSign}`
            : 'Inside another asset',
          chip: `In ${asset.parentAsset.assetID}`,
        }
      : asset.store?.name
        ? {
            value: `In ${asset.store.name}`,
            caption: asset.store.baseLocation?.name ?? 'Kit store',
            chip: `In ${asset.store.name}`,
          }
        : { value: 'Not on a vehicle', caption: 'Unassigned', chip: null }

  const holderName = asset.checkedOutTo
    ? `${asset.checkedOutTo.firstName ?? ''} ${asset.checkedOutTo.lastName ?? ''}`.trim()
    : null

  const serviceExpiry = asset.latestMaintenanceLog?.expiry
  const serviceOverdue =
    asset.requiresServicing &&
    serviceExpiry &&
    moment(serviceExpiry).isBefore(moment().startOf('day'))
  const serviceTone = !asset.requiresServicing
    ? null
    : serviceExpiry
      ? dateTone(serviceExpiry)
      : 'warning'
  const serviceCaption = !asset.requiresServicing
    ? 'No servicing needed'
    : !serviceExpiry
      ? 'No service recorded yet'
      : serviceOverdue
        ? 'Overdue'
        : serviceTone === 'warning'
          ? 'Due within a month'
          : 'In date'

  const activeDefects = asset.currentDefects?.length ?? 0

  const makeReadyLevel =
    makeReadyStatus?.level && SEVERITY[makeReadyStatus.level]
      ? makeReadyStatus.level
      : null
  const makeReadyCaption = !asset.makeReadyLoadList
    ? 'Untracked'
    : makeReadyStatus?.lastCheckedAt
      ? `Checked ${moment(makeReadyStatus.lastCheckedAt).fromNow()}`
      : 'Never checked'

  // Planned downtime: a window running now, and planned work whose end has
  // passed without maintenance being recorded.
  const activeDowntime = unavailability.find(
    (period) =>
      moment().isAfter(period.start) && moment().isBefore(period.end),
  )
  const overdueDowntime = unavailability.filter(
    (period) =>
      period.type && !period.completedAt && moment().isAfter(period.end),
  )

  const warnings = [
    activeDowntime && {
      key: 'downtime',
      label: `Unavailable now — ${
        [activeDowntime.type, activeDowntime.reason]
          .filter(Boolean)
          .join(': ') || 'downtime window'
      }`,
      color: 'error',
      onClick: () => openTab('downtime'),
    },
    overdueDowntime.length > 0 && {
      key: 'overdueDowntime',
      label: `${overdueDowntime.length} planned downtime window${
        overdueDowntime.length === 1 ? '' : 's'
      } not recorded`,
      color: 'warning',
      onClick: () => openTab('downtime'),
    },
    serviceOverdue && {
      key: 'service',
      label: 'Service overdue — record one',
      color: 'error',
      onClick: () => openDialog('maintenance'),
    },
    asset.requiresServicing &&
      !serviceExpiry && {
        key: 'noService',
        label: 'No service recorded yet — record one',
        color: 'warning',
        onClick: () => openDialog('maintenance'),
      },
    activeDefects > 0 && {
      key: 'defects',
      label: `${activeDefects} active defect${activeDefects === 1 ? '' : 's'}`,
      color: 'error',
      onClick: () => openTab('defects'),
    },
    !asset.makeReadyLoadList &&
      canAssignLoadList && {
        key: 'loadList',
        label: 'Make Ready list not set — click to fix',
        color: 'warning',
        onClick: () => openDialog('assignLoadList'),
      },
  ].filter(Boolean)

  const tiles = [
    { label: 'Location', value: location.value, caption: location.caption },
    {
      label: 'Checked Out',
      value: holderName ? (checkedOutToMe ? 'You' : holderName) : 'Nobody',
      caption: holderName ? 'Signed out' : 'Available',
    },
    {
      label: 'Service Due',
      value: asset.requiresServicing
        ? formatDate(serviceExpiry) || 'None recorded'
        : 'Not required',
      caption: serviceCaption,
      tone: serviceTone,
    },
    {
      label: 'Active Defects',
      value: `${activeDefects}`,
      tone: activeDefects === 0 ? 'success' : 'error',
    },
    makeReadyAllowed && {
      label: 'Make Ready',
      value: asset.makeReadyLoadList?.title || 'No load list',
      caption: makeReadyCaption,
      tone: makeReadyLevel ? SEVERITY[makeReadyLevel].color : null,
    },
  ].filter(Boolean)

  return (
    <Paper sx={{ p: 2, borderRadius: 3 }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 2.5,
              bgcolor: 'anMuted.main',
              color: 'contrast.main',
              // On phones the identity block stacks, so a lone icon above
              // the title just pushes everything down.
              display: { xs: 'none', md: 'grid' },
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <CategoryIcon sx={{ fontSize: 34 }} />
          </Box>
          <Box minWidth={0} flex={1}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <Typography variant="title2" fontSize={28} padding={0}>
                {asset.assetID}
              </Typography>
              {location.chip && (
                <Chip
                  size="small"
                  icon={<LocalShippingOutlinedIcon />}
                  label={location.chip}
                  color="anBlue"
                  sx={{ borderRadius: 1, fontWeight: 700 }}
                />
              )}
              {holderName && (
                <Chip
                  size="small"
                  icon={<PersonOutlineIcon />}
                  label={checkedOutToMe ? 'Checked out to you' : holderName}
                  color="anPurple"
                  sx={{ borderRadius: 1, fontWeight: 700 }}
                />
              )}
              {asset.archived && (
                <Chip size="small" label="Archived" sx={{ borderRadius: 1 }} />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {[
                asset.category,
                asset.subCategory,
                [asset.manufacturer || asset.brand, asset.model]
                  .filter(Boolean)
                  .join(' '),
                asset.serialNumber && `S/N ${asset.serialNumber}`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
            {warnings.length > 0 && (
              <Stack
                direction="row"
                spacing={0.5}
                mt={1}
                flexWrap="wrap"
                useFlexGap
              >
                {warnings.map((warning) => (
                  <Chip
                    key={warning.key}
                    size="small"
                    color={warning.color}
                    variant="outlined"
                    icon={<WarningAmberIcon />}
                    label={warning.label}
                    onClick={warning.onClick}
                    sx={{ borderRadius: 1 }}
                  />
                ))}
              </Stack>
            )}
          </Box>
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
            sx={{ flexShrink: 0, maxWidth: { md: 340, lg: 'none' } }}
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={<ReportProblemOutlinedIcon />}
              onClick={() =>
                navigate(`/report-defect?assetIDLabel=${asset.assetID}`)
              }
            >
              Defect
            </Button>
            {asset.makeReadyLoadList ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<ChecklistOutlinedIcon />}
                onClick={() => navigate('/make-ready/' + asset._id)}
              >
                Make Ready
              </Button>
            ) : (
              canAssignLoadList && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PlaylistAddOutlinedIcon />}
                  onClick={() => openDialog('assignLoadList')}
                >
                  Assign load list
                </Button>
              )
            )}
            {asset.requiresServicing && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<BuildOutlinedIcon />}
                onClick={() => openDialog('maintenance')}
              >
                Service
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<QrCode2OutlinedIcon />}
              onClick={() => openDialog('qr')}
            >
              QR Code
            </Button>
            <Button
              size="small"
              variant="outlined"
              endIcon={<MoreVertIcon />}
              onClick={(e) => setMoreAnchorEl(e.currentTarget)}
            >
              More
            </Button>
          </Stack>
        </Stack>

        <Box
          display="grid"
          gap={1.5}
          gridTemplateColumns={{
            xs: '1fr 1fr',
            sm: '1fr 1fr 1fr',
            lg: `repeat(${tiles.length}, 1fr)`,
          }}
        >
          {tiles.map((tile) => (
            <StatTile key={tile.label} {...tile} />
          ))}
        </Box>
      </Stack>

      <Menu open={!!moreAnchorEl} onClose={closeMore} anchorEl={moreAnchorEl}>
        <MenuItem
          onClick={() => {
            openDialog('edit')
            closeMore()
          }}
        >
          <ListItemIcon>
            <EditOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit asset</ListItemText>
        </MenuItem>
        {asset.checkedOutTo ? (
          <MenuItem
            disabled={!canCheckIn}
            onClick={() => {
              checkIn()
              closeMore()
            }}
          >
            <ListItemIcon>
              <LoginOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              secondary={
                canCheckIn ? null : `Checked out to ${holderName}`
              }
            >
              Check in
            </ListItemText>
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              checkOutToMe()
              closeMore()
            }}
          >
            <ListItemIcon>
              <LogoutOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Check out to me</ListItemText>
          </MenuItem>
        )}
        {asset.vehicle ? (
          <MenuItem
            onClick={() => {
              unassignVehicle()
              closeMore()
            }}
          >
            <ListItemIcon>
              <LocalShippingOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              Unassign from {asset.vehicle.callSign}
            </ListItemText>
          </MenuItem>
        ) : asset.parentAsset?.assetID ? (
          <MenuItem
            onClick={() => {
              removeFromParent()
              closeMore()
            }}
          >
            <ListItemIcon>
              <LocalShippingOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Remove from {asset.parentAsset.assetID}</ListItemText>
          </MenuItem>
        ) : asset.store?.name ? (
          <MenuItem
            onClick={() => {
              removeFromStore()
              closeMore()
            }}
          >
            <ListItemIcon>
              <WarehouseOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Remove from {asset.store.name}</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              openDialog('assignVehicle')
              closeMore()
            }}
          >
            <ListItemIcon>
              <LocalShippingOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Assign to vehicle</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            openDialog('assignStore')
            closeMore()
          }}
        >
          <ListItemIcon>
            <WarehouseOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {asset.store?.name ? 'Move to another store' : 'Move to a store'}
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            openDialog('downtime')
            closeMore()
          }}
        >
          <ListItemIcon>
            <EventBusyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Schedule downtime</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            openDialog('delete')
            closeMore()
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete asset</ListItemText>
        </MenuItem>
      </Menu>
    </Paper>
  )
}

export default AssetHero
